<template>
  <div class="bg-white dark-bg-custom">
    <v-row class="flex justify-between p-6">
      <h4 class="mb-0 text-xl font-bold">ReImbursements</h4>
      <v-icon @click="handleClose">mdi-close</v-icon>
    </v-row>
    <v-divider></v-divider>

    <!-- Year Selector -->
    <div class="px-6 pt-4">
      <v-row>
        <v-col cols="12" md="12">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="Select Year"
            outlined
            dense
            hide-details
            @change="onYearChange"
          ></v-select>
        </v-col>
      </v-row>
    </div>

    <!-- Loading indicator -->
    <v-row v-if="loading" class="mt-6">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          :color="$vuetify.theme.currentTheme.primary"
        ></v-progress-circular>
      </v-col>
    </v-row>

    <!-- Document Required Sections -->
    <div v-else class="p-2">
      <div v-if="documentSections && documentSections.length > 0">
        <div v-for="(section, sectionIndex) in documentSections" :key="sectionIndex">
          <v-card flat class="mb-6">
            <v-card-text class="p-0">
              <!-- Document Items in Tabs -->
              <v-tabs v-model="activeItemTab[sectionIndex]" @change="onTabChange(sectionIndex)">
                <v-tab
                  v-for="(item, itemIndex) in section.items"
                  :key="itemIndex"
                  class="font-weight-medium"
                >
                  {{ item.name || 'Unnamed Document' }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeItemTab[sectionIndex]">
                <v-tab-item v-for="(item, itemIndex) in section.items" :key="itemIndex">
                  <div class="mt-4">
                    <!-- Document Header with Type and Amount -->
                    <div class="mb-4">
                      <v-row>
                        <v-col cols="12" class="px-8">
                          <div class="d-flex justify-space-between align-center">
                            <div>
                              <div class="d-flex flex-wrap mt-2">
                                <v-chip class="ma-1 font-semibold" color="info" outlined>
                                  <v-icon left small>mdi-arrow-up-bold-circle</v-icon>
                                  Total Limit: ₹{{ formatCurrency(item.amount || 0) }}
                                </v-chip>
                                <v-chip class="ma-1 font-semibold" color="warning" outlined>
                                  <v-icon left small>mdi-scale-balance</v-icon>
                                  Balance Limit: ₹{{
                                    formatCurrency(getBalanceLimit(sectionIndex, itemIndex) || 0)
                                  }}
                                </v-chip>
                                <v-chip class="ma-1 font-semibold" color="purple" outlined>
                                  <v-icon left small>mdi-cash-refund</v-icon>
                                  Reimbursed: ₹{{
                                    formatCurrency(
                                      getReimbursedAmount(sectionIndex, itemIndex) || 0
                                    )
                                  }}
                                </v-chip>
                              </div>
                            </div>
                            <div class="d-flex">
                              <v-btn
                                color="primary"
                                outlined
                                @click="addNewEntry(sectionIndex, itemIndex)"
                                small
                                class="mr-2"
                              >
                                <v-icon left small>mdi-plus</v-icon>
                                Add
                              </v-btn>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Document Entries using v-row and v-col -->
                    <div class="mt-4 px-8">
                      <!-- Entry Rows -->
                      <v-card
                        v-for="(entry, entryIndex) in getDocumentEntries(sectionIndex, itemIndex)"
                        :key="entryIndex"
                        outlined
                        class="mb-2"
                      >
                        <v-card-text class="px-4 py-2">
                          <v-row align="center">
                            <v-col cols="12" md="2">
                              <v-select
                                v-model="entry.month"
                                :items="getAvailableMonths(sectionIndex, itemIndex, entry)"
                                label="Month"
                                outlined
                                dense
                                hide-details
                                clearable
                              ></v-select>
                            </v-col>
                            <v-col cols="12" md="2">
                              <v-text-field
                                v-model="entry.billNumber"
                                label="Bill Number"
                                outlined
                                dense
                                hide-details
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="2">
                              <v-menu
                                v-model="entry.menu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    :value="formatDate(entry.billDate)"
                                    label="Bill Date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    v-bind="attrs"
                                    v-on="on"
                                  ></v-text-field>
                                </template>
                                <v-date-picker
                                  no-title
                                  scrollable
                                  v-model="entry.billDate"
                                  @input="entry.menu = false"
                                  :min="getYearStartDate()"
                                  :max="getYearEndDate()"
                                ></v-date-picker>
                              </v-menu>
                            </v-col>
                            <v-col cols="12" md="1">
                              <v-text-field
                                v-model="entry.claimAmount"
                                label="Claim Amount"
                                type="number"
                                outlined
                                dense
                                hide-details
                                prefix="₹"
                                :error="isClaimAmountExceedingLimit(sectionIndex, itemIndex, entry)"
                                :error-messages="
                                  getClaimAmountErrorMessage(sectionIndex, itemIndex, entry)
                                "
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="2">
                              <v-text-field
                                v-model="entry.comments"
                                label="Comments"
                                outlined
                                dense
                                hide-details
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="1">
                              <v-chip
                                small
                                :color="getStatusColor(entry.status)"
                                text-color="white"
                                class="justify-center w-full"
                              >
                                {{ entry.status || 'Pending' }}
                              </v-chip>
                            </v-col>
                            <v-col cols="12" md="1" class="d-flex justify-center">
                              <div class="d-flex align-center">
                                <v-btn
                                  v-if="
                                    !getEntryDocumentFileName(
                                      sectionIndex,
                                      itemIndex,
                                      entryIndex
                                    ) && !getEntryDocumentUrl(sectionIndex, itemIndex, entryIndex)
                                  "
                                  outlined
                                  class="normal-case"
                                  small
                                  @click="
                                    triggerEntryDocumentUpload(sectionIndex, itemIndex, entryIndex)
                                  "
                                >
                                  <v-icon small>mdi-upload</v-icon>
                                  Upload
                                </v-btn>

                                <!-- File Name with Clear Button (file selected but not uploaded) -->
                                <div
                                  v-else-if="
                                    getEntryDocumentFileName(sectionIndex, itemIndex, entryIndex) &&
                                    !getEntryDocumentUrl(sectionIndex, itemIndex, entryIndex)
                                  "
                                  class="d-flex align-center"
                                >
                                  <div class="file-name-container">
                                    <span class="file-name-text">{{
                                      getEntryDocumentFileName(sectionIndex, itemIndex, entryIndex)
                                    }}</span>
                                  </div>
                                  <v-btn
                                    icon
                                    small
                                    @click="clearEntryDocument(sectionIndex, itemIndex, entryIndex)"
                                    class="ml-1"
                                  >
                                    <v-icon small>mdi-close</v-icon>
                                  </v-btn>
                                </div>

                                <!-- View Icon (file uploaded successfully) -->
                                <v-btn
                                  v-else-if="
                                    getEntryDocumentUrl(sectionIndex, itemIndex, entryIndex)
                                  "
                                  outlined
                                  small
                                  class="normal-case"
                                  @click="
                                    viewDocument(
                                      getEntryDocumentUrl(sectionIndex, itemIndex, entryIndex)
                                    )
                                  "
                                  color="success"
                                >
                                  <v-icon small>mdi-eye</v-icon>
                                  View
                                </v-btn>
                              </div>

                              <!-- Hidden File Input for Individual Entry -->
                              <input
                                :ref="`fileInput-${sectionIndex}-${itemIndex}-${entryIndex}`"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                @change="
                                  handleEntryDocumentUpload(
                                    $event,
                                    sectionIndex,
                                    itemIndex,
                                    entryIndex
                                  )
                                "
                                class="d-none"
                              />
                            </v-col>
                            <v-col cols="12" md="1" class="d-flex justify-center">
                              <v-btn
                                icon
                                @click="removeEntry(sectionIndex, itemIndex, entryIndex)"
                                color="error"
                                small
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </div>

                    <!-- Save Button -->
                    <div class="mt-4 px-8 flex justify-end">
                      <v-btn
                        v-if="getDocumentEntries(sectionIndex, itemIndex).length > 0"
                        color="primary"
                        small
                        @click="saveEntries(sectionIndex, itemIndex)"
                        :loading="saving[sectionIndex]?.[itemIndex]"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save
                      </v-btn>
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </div>
      </div>
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
        <p class="mt-2 grey--text">No documents required for this employee</p>
      </div>
    </div>

    <!-- Document Viewer Dialog -->
    <v-dialog v-model="showDocumentDialog" max-width="800">
      <v-card>
        <v-card-title>
          Document Preview
          <v-spacer></v-spacer>
          <v-icon @click="showDocumentDialog = false" color="white">mdi-close</v-icon>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="d-flex justify-center pa-6">
          <v-img :src="documentImageUrl" max-height="600" contain class="rounded"></v-img>
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn color="primary" @click="showDocumentDialog = false" small>Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'DocumentRequired',
  props: {
    employeeData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      activeItemTab: {},
      documentSections: [],
      documentEntries: {},
      documentFiles: {}, // Store actual File objects
      temporaryFileNames: {}, // Store temporary file names for display
      entryDocuments: {}, // Store document URLs after successful upload
      showDocumentDialog: false,
      documentImageUrl: '',
      salaryTemplate: null,
      availableMonths: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ],
      saving: {}, // Track saving state for each section and item
      baseUrl: process.env.VUE_APP_S3_BASE_URL, // Add baseUrl for S3 images
      existingReimbursements: {}, // Store existing reimbursements for calculations
      selectedYear: new Date().getFullYear(), // Default to current year
      yearOptions: this.generateYearOptions() // Generate year options
    }
  },
  computed: {
    monthOptions() {
      return this.availableMonths.map((month, index) => ({
        text: month,
        value: index
      }))
    }
  },
  async mounted() {
    if (this.employeeData) {
      await this.fetchDocumentRequiredData()
      this.processDocumentRequiredItems()
      await this.fetchExistingReimbursements()
      // Add default entry if no data exists
      this.addDefaultEntryIfNeeded()
    }
  },
  watch: {
    employeeData: {
      handler(newVal) {
        if (newVal) {
          this.refreshData()
        }
      },
      deep: true
    }
  },
  methods: {
    generateYearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year <= currentYear + 1; year++) {
        years.push({ text: year.toString(), value: year })
      }
      return years
    },

    handleClose() {
      this.$emit('handleDocumentRequiredClose')
      this.documentSections = []
    },
    async refreshData() {
      if (this.employeeData) {
        this.loading = true
        try {
          await this.fetchDocumentRequiredData()
          await this.fetchExistingReimbursements()
          this.processDocumentRequiredItems()

          this.addDefaultEntryIfNeeded()
        } catch (error) {
          console.error('Error refreshing data:', error)
        } finally {
          this.loading = false
        }
      }
    },

    // Handle year change
    async onYearChange() {
      this.loading = true
      try {
        await this.fetchDocumentRequiredData()
        await this.fetchExistingReimbursements()
        this.processDocumentRequiredItems()
      } catch (error) {
        console.error('Error on year change:', error)
      } finally {
        this.loading = false
      }
    },

    // Handle tab change - fetch data for selected tab only
    async onTabChange(sectionIndex) {
      const itemIndex = this.activeItemTab[sectionIndex]
      if (sectionIndex !== undefined && itemIndex !== undefined) {
        const item = this.documentSections[sectionIndex].items[itemIndex]
        if (item) {
          await this.fetchExistingReimbursements(item._id)
        }
      }
    },

    addDefaultEntryIfNeeded() {
      if (this.documentSections && this.documentSections.length > 0) {
        this.documentSections.forEach((section, sectionIndex) => {
          if (section.items && section.items.length > 0) {
            section.items.forEach((item, itemIndex) => {
              const entries = this.getDocumentEntries(sectionIndex, itemIndex)
              // If no entries exist, add one default entry
              if (entries.length === 0) {
                this.addNewEntry(sectionIndex, itemIndex)
              }
            })
          }
        })
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      return this.$dayjs(dateString).format('DD/MM/YYYY')
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00'
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 2
      }).format(value)
    },
    async fetchDocumentRequiredData() {
      if (!this.employeeData) {
        return
      }

      this.loading = true
      try {
        const designationId =
          this.employeeData && this.employeeData.designation
            ? this.employeeData.designation._id
            : null

        if (!designationId) {
          throw new Error('Employee role not found')
        }
        const params = {
          designation: designationId,
          year: this.selectedYear
        }

        const response = await this.$axios.get(`/org/salary-templates`, { params })
        const salaryTemplates = response.data.salaryTemplates

        // if (!salaryTemplates || salaryTemplates.length === 0) {
        //   throw new Error('No salary template found for this role')
        // }

        this.salaryTemplate = salaryTemplates[0]
        this.processDocumentRequiredItems()
      } catch (error) {
        console.error('Error fetching document required data:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchExistingReimbursements(reimbursementType = '') {
      if (!this.employeeData || !this.employeeData._id) {
        return
      }

      try {
        const params = {
          year: this.selectedYear,
          reimbursementType:
            reimbursementType || this.salaryTemplate?.taxSavingComponents[0]?._id || undefined
        }

        const response = await this.$axios.get(
          `/orgSetup/reimbursement/user/${this.employeeData._id}`,
          { params }
        )
        const reimbursements = response.data.reimbursements || []

        // Store existing reimbursements for calculations
        this.existingReimbursements = {}
        reimbursements.forEach((reimbursement) => {
          if (!this.existingReimbursements[reimbursement.reimbursementType]) {
            this.$set(this.existingReimbursements, reimbursement.reimbursementType, [])
          }
          this.existingReimbursements[reimbursement.reimbursementType].push(reimbursement)
        })

        // Clear existing data first
        this.documentEntries = {} // Clear form entries as well
        this.entryDocuments = {} // Clear entry documents

        // Process each reimbursement as a separate entry
        for (const reimbursement of reimbursements) {
          // Find the matching section and item
          let foundSectionIndex = -1
          let foundItemIndex = -1

          for (let sectionIndex = 0; sectionIndex < this.documentSections.length; sectionIndex++) {
            const section = this.documentSections[sectionIndex]
            for (let itemIndex = 0; itemIndex < section.items.length; itemIndex++) {
              const item = section.items[itemIndex]
              // Match by reimbursementType ID
              if (item._id === reimbursement.reimbursementType) {
                foundSectionIndex = sectionIndex
                foundItemIndex = itemIndex
                break
              }
            }
            if (foundSectionIndex !== -1) break
          }

          // If we found a matching section/item, add the reimbursement to form entries
          if (foundSectionIndex !== -1 && foundItemIndex !== -1) {
            const key = this.getEntryKey(foundSectionIndex, foundItemIndex)

            // Initialize document entries array for this section/item
            if (!this.documentEntries[key]) {
              this.$set(this.documentEntries, key, [])
            }

            // Add to form entries (for editing)
            const formEntry = {
              id: reimbursement._id, // Store ID for updates
              month: this.getMonthIndex(reimbursement.month),
              billNumber: reimbursement.billNumber || '',
              billDate: reimbursement.billDate || '',
              claimAmount: reimbursement.claimAmount || '',
              comments:
                Array.isArray(reimbursement.comments) && reimbursement.comments.length > 0
                  ? reimbursement.comments[reimbursement.comments.length - 1].message
                  : reimbursement.comments || '',
              status:
                Array.isArray(reimbursement.approvals) && reimbursement.approvals.length > 0
                  ? reimbursement.approvals[reimbursement.approvals.length - 1].status
                  : 'Pending',
              menu: false
            }

            this.documentEntries[key].push(formEntry)

            // If there's an attached bill, store it for the entry
            if (reimbursement.attachedBill) {
              const entryIndex = this.documentEntries[key].length - 1
              const entryKey = `${key}-${entryIndex}`
              this.$set(this.entryDocuments, entryKey, reimbursement.attachedBill)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching existing reimbursements:', error)
      }
    },
    getMonthIndex(monthName) {
      return this.availableMonths.indexOf(monthName)
    },
    processDocumentRequiredItems() {
      const sections = []

      if (
        this.salaryTemplate &&
        this.salaryTemplate.taxSavingComponents &&
        Array.isArray(this.salaryTemplate.taxSavingComponents) &&
        this.salaryTemplate.taxSavingComponents.length > 0
      ) {
        const documentRequiredItems = this.salaryTemplate.taxSavingComponents
          .filter((item) => item && item.documentRequired)
          .map((item) => ({
            _id: item._id, // Use the _id as reimbursementType
            name: item.componentName || 'Unnamed Investment',
            amount: parseFloat(item.amount) || 0,
            documentUploaded: false
          }))

        if (documentRequiredItems.length > 0) {
          sections.push({
            title: 'Document Entries',
            items: documentRequiredItems
          })
        }
      }

      this.documentSections = sections
      // Initialize activeItemTab for each section
      sections.forEach((_, index) => {
        this.$set(this.activeItemTab, index, 0)
      })
    },
    getEntryKey(sectionIndex, itemIndex) {
      return `${sectionIndex}-${itemIndex}`
    },
    getDocumentEntries(sectionIndex, itemIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      if (!this.documentEntries[key]) {
        this.$set(this.documentEntries, key, [])
      }
      return this.documentEntries[key]
    },
    getAvailableMonths(sectionIndex, itemIndex, entry) {
      // Get all entries for this section and item
      const key = this.getEntryKey(sectionIndex, itemIndex)
      const entries = this.documentEntries[key] || []

      // Get all selected months except the current entry being edited
      const selectedMonths = entries
        .filter((e) => e !== entry && e.month !== null && e.month !== undefined)
        .map((e) => e.month)

      // Get current month (0-11, so add 1 to get 1-12)
      const currentMonth = new Date().getMonth()

      // Return only months that haven't been selected yet and are not greater than current month
      return this.monthOptions.filter(
        (month) => !selectedMonths.includes(month.value) && month.value <= currentMonth
      )
    },
    // Calculate total limit for an item
    getTotalLimit(sectionIndex, itemIndex) {
      const item = this.documentSections[sectionIndex].items[itemIndex]
      return item.amount || 0
    },

    // Calculate balance limit using the formula from memory
    getBalanceLimit(sectionIndex, itemIndex) {
      const totalLimit = this.getTotalLimit(sectionIndex, itemIndex)

      // Using the formula from memory: (total annual limit / 12) * current month number
      const monthlyLimit = totalLimit / 12
      const currentMonth = new Date().getMonth() + 1 // Get current month (1-12)

      // Calculate balance limit
      const balanceLimit = monthlyLimit * currentMonth

      // Subtract already reimbursed amount
      const reimbursedAmount = this.getReimbursedAmount(sectionIndex, itemIndex)
      return Math.max(0, balanceLimit - reimbursedAmount)
    },

    // Calculate reimbursed amount from existing reimbursements
    getReimbursedAmount(sectionIndex, itemIndex) {
      const item = this.documentSections[sectionIndex].items[itemIndex]

      // Get all reimbursements for this item type
      const reimbursements = this.existingReimbursements[item._id] || []

      // Sum up all claim amounts
      return reimbursements.reduce((sum, reimbursement) => {
        return sum + (parseFloat(reimbursement.claimAmount) || 0)
      }, 0)
    },

    // Method to check if claim amount exceeds the balance limit for the entry
    isClaimAmountExceedingLimit(sectionIndex, itemIndex, entry) {
      if (!entry.claimAmount || isNaN(parseFloat(entry.claimAmount))) {
        return false
      }

      const claimAmount = parseFloat(entry.claimAmount)
      const balanceLimit = this.getBalanceLimit(sectionIndex, itemIndex)

      // Also consider other entries in the same section when calculating remaining balance
      const entries = this.getDocumentEntries(sectionIndex, itemIndex)
      const otherEntriesTotal = entries
        .filter((e) => e !== entry && e.claimAmount && !isNaN(parseFloat(e.claimAmount)))
        .reduce((sum, e) => sum + parseFloat(e.claimAmount), 0)

      const remainingBalance = balanceLimit - otherEntriesTotal

      return claimAmount > remainingBalance
    },

    // Method to get error message for claim amount
    getClaimAmountErrorMessage(sectionIndex, itemIndex, entry) {
      if (!this.isClaimAmountExceedingLimit(sectionIndex, itemIndex, entry)) {
        return []
      }

      const claimAmount = parseFloat(entry.claimAmount)
      const balanceLimit = this.getBalanceLimit(sectionIndex, itemIndex)

      // Also consider other entries in the same section when calculating remaining balance
      const entries = this.getDocumentEntries(sectionIndex, itemIndex)
      const otherEntriesTotal = entries
        .filter((e) => e !== entry && e.claimAmount && !isNaN(parseFloat(e.claimAmount)))
        .reduce((sum, e) => sum + parseFloat(e.claimAmount), 0)

      const remainingBalance = balanceLimit - otherEntriesTotal

      return [`Amount exceeds remaining balance limit of ₹${this.formatCurrency(remainingBalance)}`]
    },
    addNewEntry(sectionIndex, itemIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      if (!this.documentEntries[key]) {
        this.$set(this.documentEntries, key, [])
      }

      const newEntry = {
        month: null,
        billNumber: '',
        billDate: '',
        claimAmount: '',
        comments: '',
        status: 'Pending',
        menu: false
      }

      this.documentEntries[key].push(newEntry)
    },
    removeEntry(sectionIndex, itemIndex, entryIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      if (this.documentEntries[key] && this.documentEntries[key][entryIndex]) {
        this.$delete(this.documentEntries[key], entryIndex)

        // Also remove any associated document
        const entryKey = `${key}-${entryIndex}`
        if (this.entryDocuments[entryKey]) {
          this.$delete(this.entryDocuments, entryKey)
        }

        // Also remove temporary file name
        if (this.temporaryFileNames[entryKey]) {
          this.$delete(this.temporaryFileNames, entryKey)
        }

        // Also remove document file
        if (this.documentFiles[entryKey]) {
          this.$delete(this.documentFiles, entryKey)
        }
      }
    },

    // Trigger file upload dialog for individual entry
    triggerEntryDocumentUpload(sectionIndex, itemIndex, entryIndex) {
      const refKey = `fileInput-${sectionIndex}-${itemIndex}-${entryIndex}`
      // Access the hidden file input and trigger click
      const fileInput = this.$refs[refKey]
      if (fileInput && fileInput[0]) {
        fileInput[0].click()
      }
    },

    // Get temporary file name for display (before upload)
    getEntryDocumentFileName(sectionIndex, itemIndex, entryIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      const entryKey = `${key}-${entryIndex}`
      return this.temporaryFileNames[entryKey] || null
    },

    // Handle file upload for individual entry
    handleEntryDocumentUpload(event, sectionIndex, itemIndex, entryIndex) {
      const file = event.target.files[0]
      if (file) {
        const key = this.getEntryKey(sectionIndex, itemIndex)
        const entryKey = `${key}-${entryIndex}`

        // Store the file temporarily
        this.$set(this.documentFiles, entryKey, file)

        // Store the file name for display
        this.$set(this.temporaryFileNames, entryKey, file.name)

        // Clear any existing document URL for this entry
        if (this.entryDocuments[entryKey]) {
          this.$delete(this.entryDocuments, entryKey)
        }
      }

      // Reset the file input
      event.target.value = ''
    },

    // Get document URL for an entry
    getEntryDocumentUrl(sectionIndex, itemIndex, entryIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      const entryKey = `${key}-${entryIndex}`
      return this.entryDocuments[entryKey] || null
    },

    // Clear selected document (before upload)
    clearEntryDocument(sectionIndex, itemIndex, entryIndex) {
      const key = this.getEntryKey(sectionIndex, itemIndex)
      const entryKey = `${key}-${entryIndex}`

      // Remove temporary file name
      if (this.temporaryFileNames[entryKey]) {
        this.$delete(this.temporaryFileNames, entryKey)
      }

      // Remove file from documentFiles
      if (this.documentFiles[entryKey]) {
        this.$delete(this.documentFiles, entryKey)
      }
    },

    // Save entries - simplified for the new model
    async saveEntries(sectionIndex, itemIndex) {
      // Initialize saving state tracking
      if (!this.saving[sectionIndex]) {
        this.$set(this.saving, sectionIndex, {})
      }
      this.$set(this.saving[sectionIndex], itemIndex, true)

      try {
        // Get the item details
        const item = this.documentSections[sectionIndex].items[itemIndex]
        const entries = this.getDocumentEntries(sectionIndex, itemIndex)

        // Filter valid entries
        const validEntries = entries.filter(
          (entry) =>
            entry.month !== null &&
            entry.month !== undefined &&
            entry.claimAmount &&
            !isNaN(parseFloat(entry.claimAmount))
        )

        if (validEntries.length === 0) {
          this.$toast.error('Please fill in at least one valid entry before saving')
          return
        }

        // Check if total claimed amount exceeds balance limit
        const totalClaimedAmount = validEntries.reduce((sum, entry) => {
          return sum + parseFloat(entry.claimAmount || 0)
        }, 0)

        const balanceLimit = this.getBalanceLimit(sectionIndex, itemIndex)

        // Also consider existing entries when calculating remaining balance
        const allEntries = this.getDocumentEntries(sectionIndex, itemIndex)
        const existingEntriesTotal = allEntries
          .filter(
            (e) => !validEntries.includes(e) && e.claimAmount && !isNaN(parseFloat(e.claimAmount))
          )
          .reduce((sum, e) => sum + parseFloat(e.claimAmount), 0)

        const remainingBalance = balanceLimit - existingEntriesTotal

        if (totalClaimedAmount > remainingBalance) {
          this.$toast.error(
            `Total claimed amount (₹${this.formatCurrency(
              totalClaimedAmount
            )}) exceeds the remaining balance limit (₹${this.formatCurrency(
              remainingBalance
            )}) for current month`
          )
          return
        }

        // Handle existing entries (update them)
        for (const [entryIndex, entry] of validEntries.entries()) {
          // Find the actual index in the original array
          const actualEntryIndex = entries.indexOf(entry)

          // Check if there's a document file to upload for this entry
          const key = this.getEntryKey(sectionIndex, itemIndex)
          const entryKey = `${key}-${actualEntryIndex}`
          let attachedBill = null

          if (this.documentFiles[entryKey]) {
            // Upload the document for this specific entry
            attachedBill = await this.uploadEntryDocument(this.documentFiles[entryKey], entry)

            // After successful upload, move from temporary to permanent storage
            if (attachedBill) {
              // Store the URL in entryDocuments
              this.$set(this.entryDocuments, entryKey, attachedBill)

              // Clear temporary file name
              if (this.temporaryFileNames[entryKey]) {
                this.$delete(this.temporaryFileNames, entryKey)
              }

              // Clear the file object
              if (this.documentFiles[entryKey]) {
                this.$delete(this.documentFiles, entryKey)
              }
            }
          } else if (this.entryDocuments[entryKey] && this.entryDocuments[entryKey].includes('/')) {
            // Use existing document URL if available
            attachedBill = this.entryDocuments[entryKey]
          }

          const reimbursementData = {
            user: this.employeeData._id,
            salaryTemplate: this.salaryTemplate._id,
            reimbursementType: item._id,
            attachedBill: attachedBill || undefined,
            year: this.selectedYear, // Use selected year instead of current year
            month: this.availableMonths[entry.month],
            billNumber: entry.billNumber || undefined,
            billDate: entry.billDate || undefined,
            claimAmount: parseFloat(entry.claimAmount),
            comments: entry.comments
              ? [
                  {
                    message: entry.comments,
                    commentedBy: this.$storage.getUniversal('user')?._id,
                    commentedAt: new Date()
                  }
                ]
              : [],
            approvals: [
              {
                status: 'Pending'
              }
            ]
          }

          if (entry.id !== undefined) {
            // Update existing reimbursement (PUT)
            await this.$axios.put(`/orgSetup/reimbursement/${entry.id}`, reimbursementData)
          } else {
            // Create new reimbursement (POST) - each entry is its own document
            await this.$axios.post('/orgSetup/reimbursement', reimbursementData)
          }
        }

        // Clear the file inputs after saving
        Object.keys(this.documentFiles).forEach((key) => {
          this.$set(this.documentFiles, key, null)
        })

        this.$toast.success('Entries saved successfully')

        // Refresh data to show the newly created reimbursements
        await this.refreshData()
      } catch (error) {
        console.error('Error saving entries:', error)
        this.$toast.error(
          'Error saving entries: ' + (error.response?.data?.message || error.message)
        )
      } finally {
        this.$set(this.saving[sectionIndex], itemIndex, false)
      }
    },

    // View document
    viewDocument(documentUrl) {
      if (documentUrl) {
        // Check if the documentUrl is already a full URL
        if (documentUrl.startsWith('http')) {
          this.documentImageUrl = documentUrl
        } else if (documentUrl.includes('/')) {
          // If it looks like a path, prepend the S3 base URL
          this.documentImageUrl = `${this.baseUrl || ''}${documentUrl}`
        } else {
          // If it's just a file name, show a placeholder
          this.documentImageUrl =
            'https://via.placeholder.com/600x400/000000/FFFFFF?text=Document+Preview'
        }
      } else {
        // Fallback to dummy image if no documentUrl
        this.documentImageUrl =
          'https://via.placeholder.com/600x400/000000/FFFFFF?text=Document+Preview'
      }
      this.showDocumentDialog = true
    },

    // Upload document for individual entry
    async uploadEntryDocument(file, entry) {
      try {
        const formData = new FormData()
        const monthName = this.availableMonths[entry.month]
        const formattedMonth = monthName.toLowerCase()
        formData.append('attachments', file)
        formData.append('month', formattedMonth)
        formData.append('userId', this.employeeData._id)

        const response = await this.$axios.post('/reimbursement/document', formData)

        return response.data.url?.doc_physical_path
      } catch (error) {
        console.error('Error uploading document:', error)
        this.$toast.error('Failed to upload document')
        throw error
      }
    },

    // New methods for date restriction
    getYearStartDate() {
      return `${this.selectedYear}-01-01`
    },
    getYearEndDate() {
      return `${this.selectedYear}-12-31`
    },
    getStatusColor(status) {
      switch (status) {
        case 'Approved':
          return 'success'
        case 'Rejected':
          return 'error'
        case 'Pending':
          return 'warning'
        case 'Review':
          return 'info'
        case 'Escalated':
          return 'deep-purple'
        case 'Resubmitted':
          return 'teal'
        default:
          return 'grey'
      }
    }
  }
}
</script>

<style scoped>
.file-name-container {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
}

.file-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
